/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import * as React from 'react';
import {
    desktopImagePosition,
    desktopLayoutType,
    INavigationMenuProps,
    inheritCategoryImages,
    navigationMenuSource,
    pagerType
} from '../definition-extensions/navigation-menu.ext.props.autogenerated';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Render the Navigation menu.
 * @param props - The navigation menu view props.
 * @returns Return HTML having INavigationMenuViewProps props with navigation menu.
 */
const navigationMenuView = (props: INavigationMenuViewProps & INavigationMenuProps<{}>): JSX.Element => {
    return <NavMenu navProps={props} />;
};

/**
 * INavigationMenuViewRootProps.
 */
interface INavigationMenuViewRootProps {
    navProps: INavigationMenuViewProps & INavigationMenuProps<{}>;
}

/**
 * Represent navigation menu state interface.
 */
interface INavigationState {
    categoryImages: Msdyn365.IImageData[];
    imageOffset: number;
    screenWidth: number;
    isMobile: boolean;
    isNavOpen: boolean;
    breadcrumb: IMenuItemData[];
}

class NavMenu extends React.PureComponent<INavigationMenuViewRootProps, INavigationState> {
    private readonly mobileMaxWidth = 1200;
    private readonly widthPerImage = 250;
    private parentCategoryMap: Map<IMenuItemData, IMenuItemData> = new Map();

    public constructor(props: INavigationMenuViewRootProps) {
        super(props);

        this.state = {
            categoryImages: [],
            imageOffset: 0,
            screenWidth: window.innerWidth,
            isMobile: this.isMobile,
            isNavOpen: false,
            breadcrumb: []
        };

        const handleResize = () =>
            this.setState({ ...this.state, isMobile: this.isMobile, screenWidth: window.innerWidth, imageOffset: 0 });
        window.addEventListener('resize', handleResize);

        for (let menu of this.getMenus()) {
            this.mapParentCategory(menu);
        }
    }

    private get isMobile(): boolean {
        return window.innerWidth <= this.mobileMaxWidth;
    }

    public render(): JSX.Element | null {
        return <div className='nss-navigation-menu'>{this.state.isMobile ? this._renderMobile() : this._renderDesktop()}</div>;
    }

    private mapParentCategory(parent?: IMenuItemData): void {
        if (!parent?.subMenu) {
            return;
        }

        for (let menu of parent.subMenu) {
            this.parentCategoryMap.set(menu, parent);
            this.mapParentCategory(menu);
        }
    }

    private getParentCategory(menu?: IMenuItemData): IMenuItemData | undefined {
        if (!menu) {
            return undefined;
        }

        return this.parentCategoryMap.get(menu);
    }

    private getMenus(): IMenuItemData[] {
        switch (Object(this.props.navProps.config.navigationMenuSource)?.toString()) {
            case navigationMenuSource.all:
                return this.props.navProps.menuItemData;
            case navigationMenuSource.retailServerOnly:
                return this.props.navProps.menuItemData.filter(menu => menu.menuSource === 'Retail');
            case navigationMenuSource.cmsOnly:
                return this.props.navProps.menuItemData.filter(menu => menu.menuSource === 'Cms');
            default:
                // scream in typscript pain
                return [];
        }
    }

    private _renderDesktop(): JSX.Element | null {
        const _handleOutsideClick = () => {
            const elements = document.querySelectorAll('.nss-navigation-menu__sub-menu');
            for (let i = 0; i < elements.length; i++) {
                elements.item(i).classList.remove('shown');
            }

            const elements2 = document.querySelectorAll('.nss-navigation-menu a');
            for (let i = 0; i < elements2.length; i++) {
                elements2.item(i).classList.remove('selected');
            }

            document.querySelector('.nss-navigation-menu__wrap')?.classList.remove('open');
            this._updateCategoryImage();
        };

        const _handleAnchorClick = (event: React.MouseEvent<HTMLElement>) => {
            if (event.target instanceof HTMLElement) {
                if (!window.matchMedia('(hover: none)').matches || event.target.classList.contains('touched')) {
                    return true;
                }
            }

            event.preventDefault();
            event.stopPropagation();
            return false;
        };

        const _handleTouchEnd = (event: React.TouchEvent<HTMLElement>, menu: IMenuItemData, level: number) => {
            if (event.target instanceof HTMLElement) {
                if (event.target.classList.contains('touched')) {
                    return;
                }

                // not selected, emulate hover
                _onLinkMouseEnter(event, menu, level);
                event.target.classList.add('touched');
                document.querySelector('.nss-navigation-menu__wrap')?.classList.add('open');
            }
        };

        const _onLinkMouseEnter = (
            event: React.MouseEvent<HTMLElement, MouseEvent> | React.TouchEvent<HTMLElement>,
            menu: IMenuItemData,
            level: number
        ) => {
            // remove other submenus of the set level
            const elements = document.querySelectorAll('.nss-navigation-menu__sub-menu');
            for (let i = 0; i < elements.length; i++) {
                if (Number(elements.item(i).getAttribute('data-level')) > level) {
                    elements.item(i).classList.remove('shown');
                }
            }

            // show the hovered submenu
            document.getElementById(`nss-navigation-submenu-details-${menu.id}`)?.classList.add('shown');

            // remove other selections of the set level
            const elements2 = document.querySelectorAll('.nss-navigation-menu a');
            for (let i = 0; i < elements2.length; i++) {
                if (Number(elements2.item(i).getAttribute('data-level')) >= level) {
                    elements2.item(i).classList.remove('selected');
                    elements2.item(i).classList.remove('touched');
                }
            }

            // select the target element
            if (event.target instanceof HTMLElement) {
                event.target.classList.add('selected');
            }

            this._updateCategoryImage(menu);
        };

        const _renderMainMenu = (mainMenus: IMenuItemData[]): JSX.Element | null => {
            return (
                <div className='nss-navigation-menu__main-menu'>
                    {mainMenus.map(menu => (
                        <a
                            key={menu.id}
                            onMouseEnter={event => _onLinkMouseEnter(event, menu, 0)}
                            onTouchEnd={event => _handleTouchEnd(event, menu, 0)}
                            onClick={event => _handleAnchorClick(event)}
                            href={menu.linkURL}
                            data-level='0'
                        >
                            {menu.linkText}
                        </a>
                    ))}
                </div>
            );
        };

        const _renderNestedSubMenu = (parent: IMenuItemData, menus?: IMenuItemData[], level: number = 1): JSX.Element | null => {
            if (!menus?.length) {
                return null;
            }

            return (
                <>
                    <div data-level={level} className='nss-navigation-menu__sub-menu' id={`nss-navigation-submenu-details-${parent.id}`}>
                        {menus.map(menu => (
                            <a
                                key={menu.id}
                                onMouseEnter={event => _onLinkMouseEnter(event, menu, level)}
                                onTouchEnd={event => _handleTouchEnd(event, menu, level)}
                                onClick={event => _handleAnchorClick(event)}
                                href={menu.linkURL}
                                data-level={level}
                            >
                                {menu.linkText}
                            </a>
                        ))}
                    </div>
                    {menus.map(menu => _renderNestedSubMenu(menu, menu.subMenu, level + 1))}
                </>
            );
        };

        const _renderFlatSubMenu = (parent: IMenuItemData, submenus?: IMenuItemData[]): JSX.Element => {
            return (
                <div data-level='1' className='nss-navigation-menu__sub-menu' id={`nss-navigation-submenu-details-${parent.id}`}>
                    {submenus?.map(menu => (
                        <div className='nss-navigation-menu__sub-menu-flat-wrap'>
                            <a
                                key={menu.id}
                                onMouseEnter={event => _onLinkMouseEnter(event, menu, 1)}
                                onTouchEnd={event => _handleTouchEnd(event, menu, 1)}
                                onClick={event => _handleAnchorClick(event)}
                                href={menu.linkURL}
                                data-level={1}
                            >
                                {menu.linkText}
                            </a>
                            {!!menu?.subMenu?.length && (
                                <div className='nss-navigation-menu__sub-menu-flat-contents'>
                                    {menu.subMenu.map(sub => (
                                        <a
                                            key={sub.id}
                                            onMouseEnter={event => _onLinkMouseEnter(event, sub, 2)}
                                            onTouchEnd={event => _handleTouchEnd(event, sub, 2)}
                                            onClick={event => _handleAnchorClick(event)}
                                            href={sub.linkURL}
                                            data-level={2}
                                        >
                                            {sub.linkText}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        };

        const _renderNavbar = (): JSX.Element => {
            const _renderNestedRootEnabled = (): JSX.Element => {
                const mainMenu = this.getMenus()[0];
                const mainSubmenus = this.getMenus()[0].subMenu;
                const categoryContent = mainSubmenus?.filter(menu => menu.menuSource === 'Retail');
                const cmsContent = mainSubmenus?.filter(menu => menu.menuSource === 'Cms') ?? [];
                const submenus: IMenuItemData[] = [];
                if (categoryContent) submenus.push(...categoryContent);
                for (const menu of cmsContent) {
                    if (menu.subMenu) {
                        submenus.push(...menu.subMenu);
                    }
                }

                return (
                    <div className='nss-navigation-menu__wrap' onMouseLeave={_handleOutsideClick}>
                        {_renderMainMenu([mainMenu, ...cmsContent])}
                        <div className='nss-navigation-menu__sub-menus-wrap'>
                            <div className='nss-navigation-menu__sub-menus'>
                                {_renderNestedSubMenu(this.getMenus()[0], categoryContent)}
                                {cmsContent && cmsContent.map(menu => _renderNestedSubMenu(menu, menu.subMenu))}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.left && (
                                    <div className='spacer'></div>
                                )}
                                {this._getCategoryImages()}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.right && (
                                    <div className='spacer'></div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            };

            const _renderNestedRootDisabled = (): JSX.Element => {
                const mainMenus = this.getMenus();

                return (
                    <div className='nss-navigation-menu__wrap' onMouseLeave={_handleOutsideClick}>
                        {_renderMainMenu(mainMenus)}
                        <div className='nss-navigation-menu__sub-menus-wrap'>
                            <div className='nss-navigation-menu__sub-menus'>
                                {mainMenus.map(menu => _renderNestedSubMenu(menu, menu.subMenu))}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.left && (
                                    <div className='spacer'></div>
                                )}
                                {this._getCategoryImages()}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.right && (
                                    <div className='spacer'></div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            };

            const _renderFlatRootEnabled = (): JSX.Element => {
                const mainMenu = this.getMenus()[0];
                const mainSubmenus = this.getMenus()[0].subMenu;
                const categoryContent = mainSubmenus?.filter(menu => menu.menuSource === 'Retail');
                const cmsContent = mainSubmenus?.filter(menu => menu.menuSource === 'Cms') ?? [];
                const submenus: IMenuItemData[] = [];
                if (categoryContent) submenus.push(...categoryContent);
                for (const menu of cmsContent) {
                    if (menu.subMenu) {
                        submenus.push(...menu.subMenu);
                    }
                }

                return (
                    <div className='nss-navigation-menu__wrap nss-navigation-menu__flat' onMouseLeave={_handleOutsideClick}>
                        {_renderMainMenu([mainMenu, ...cmsContent])}
                        <div className='nss-navigation-menu__sub-menus-wrap'>
                            <div className='nss-navigation-menu__sub-menus'>
                                {_renderFlatSubMenu(mainMenu, categoryContent)}
                                {cmsContent.map(menu => _renderFlatSubMenu(menu, menu.subMenu))}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.left && (
                                    <div className='spacer'></div>
                                )}
                                {this._getCategoryImages(1)}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.right && (
                                    <div className='spacer'></div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            };

            const _renderFlatRootDisabled = (): JSX.Element => {
                const mainMenus = this.getMenus();

                return (
                    <div className='nss-navigation-menu__wrap nss-navigation-menu__flat' onMouseLeave={_handleOutsideClick}>
                        {_renderMainMenu(mainMenus)}
                        <div className='nss-navigation-menu__sub-menus-wrap'>
                            <div className='nss-navigation-menu__sub-menus'>
                                {mainMenus.map(menu => _renderFlatSubMenu(menu, menu.subMenu))}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.left && (
                                    <div className='spacer'></div>
                                )}
                                {this._getCategoryImages(1)}
                                {this.props.navProps.config.desktopImagePosition !== desktopImagePosition.right && (
                                    <div className='spacer'></div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            };

            if (this.props.navProps.config.desktopLayoutType === desktopLayoutType.flat) {
                if (this.props.navProps.config.enabletopMenu) {
                    return _renderFlatRootEnabled();
                }

                return _renderFlatRootDisabled();
            }

            if (this.props.navProps.config.enabletopMenu) {
                return _renderNestedRootEnabled();
            }

            return _renderNestedRootDisabled();
        };

        return _renderNavbar();
    }

    private _renderMobile(): JSX.Element | null {
        const mainMenu = this.getMenus()[0];
        const backRef = this.state.breadcrumb[this.state.breadcrumb.length - 1];

        let menus: IMenuItemData[] = [];
        if (backRef?.subMenu?.length) {
            menus = backRef.subMenu;
        } else if (this.props.navProps.config.enabletopMenu) {
            menus = mainMenu.subMenu ?? [];
        } else {
            menus = this.getMenus();
        }

        const _renderDrawerIcon = (): JSX.Element | null => {
            return (
                <div className='nss-navigation-menu__drawer-icon'>
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32px' fill='currentColor'>
                        <path d='M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z'></path>
                    </svg>
                </div>
            );
        };

        const _renderBackIcon = (): JSX.Element | null => {
            return (
                <div className='nss-navigation-menu__back-icon'>
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32px' fill='currentColor'>
                        <path d='M11.8284 12.0005L14.6569 14.8289L13.2426 16.2431L9 12.0005L13.2426 7.75781L14.6569 9.17203L11.8284 12.0005Z'></path>
                    </svg>
                </div>
            );
        };

        const _goBack = () => {
            const newBreadcrumb = this.state.breadcrumb.slice(0, -1);
            this.setState({ ...this.state, breadcrumb: newBreadcrumb });
            this._updateCategoryImage(newBreadcrumb[newBreadcrumb.length - 1]);
        };

        const _openDrawer = (menu: IMenuItemData) => {
            if (menu.subMenu?.length) {
                this.setState({ ...this.state, breadcrumb: [...this.state.breadcrumb, menu] });
                this._updateCategoryImage(menu);
            }
        };

        const _handleLinkClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            event.stopPropagation();
        };

        return (
            <div className='nss-navigation-menu__mobile'>
                {this._getCategoryImages()}
                <div className='nss-navigation-menu__sub-menus'>
                    {backRef && (
                        <div className='nss-navigation-menu__drawer-back' onClick={_goBack}>
                            {_renderBackIcon()}
                            <a href={backRef.linkURL} onClick={_handleLinkClick}>
                                {backRef.linkText}
                            </a>
                        </div>
                    )}
                    {menus.map(menu => (
                        <div className='nss-navigation-menu__drawer' onClick={() => _openDrawer(menu)}>
                            <a href={menu.linkURL} onClick={_handleLinkClick}>
                                {menu.linkText}
                            </a>
                            {!!menu.subMenu?.length && _renderDrawerIcon()}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    private checkImage(url?: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (!url) {
                resolve(false);
                return;
            }

            const img = new Image();

            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);

            img.src = url;
        });
    }

    private async _updateCategoryImage(menu?: IMenuItemData): Promise<void> {
        const _resolveCategoryImages = async (menu?: IMenuItemData): Promise<Msdyn365.IImageData[]> => {
            const linkText = menu?.linkText ? menu.linkText : '';
            const src = generateImageUrl(menu?.imageSource, this.props.navProps.context.actionContext.requestContext.apiSettings);
            const promotionalImages: Msdyn365.IImageData[] = [];

            if (this.props.navProps.config.displayCategoryImage) {
                if (await this.checkImage(src)) {
                    promotionalImages.push({ src: src, altText: linkText, href: menu?.linkURL });
                }
            }

            // Read category and promotional image in one array
            if (this.props.navProps.config.displayPromotionalImage && menu?.promotionalContent?.length) {
                for (const item of menu.promotionalContent) {
                    const imageSource = generateImageUrl(
                        item.image.src,
                        this.props.navProps.context.actionContext.requestContext.apiSettings
                    );
                    if (await this.checkImage(imageSource)) {
                        promotionalImages.push({
                            src: imageSource,
                            altText: item.text,
                            href: item.linkUrl.destinationUrl
                        });
                    }
                }
            }

            return promotionalImages;
        };

        const _shouldEvaluteParent = (images: Msdyn365.IImageData[], menu?: IMenuItemData): boolean => {
            if (!menu) {
                return false;
            }

            if (this.props.navProps.config.inheritCategoryImages === inheritCategoryImages.whenEmpty) {
                return images.length === 0;
            }

            if (this.props.navProps.config.inheritCategoryImages === inheritCategoryImages.always) {
                return true;
            }

            return false;
        };

        const promotionalImages = await _resolveCategoryImages(menu);
        if (this.props.navProps.config.inheritCategoryImages !== inheritCategoryImages.never) {
            let parent = this.getParentCategory(menu);
            while (_shouldEvaluteParent(promotionalImages, parent)) {
                const imagesToAdd = await _resolveCategoryImages(parent);
                promotionalImages.push(...imagesToAdd);
                parent = this.getParentCategory(parent);
            }
        }

        this.setState({
            categoryImages: promotionalImages?.length ? promotionalImages : [],
            imageOffset: 0
        });
    }

    private _getCategoryImages(maxImagesShown: number = 3) {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=162&h=162&m=8', w: 0, h: 0 },
                sm: { q: 'w=162&h=162&m=8', w: 0, h: 0 },
                md: { q: 'w=203&h=203&m=8', w: 0, h: 0 },
                lg: { q: 'w=203&h=203&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };

        const imageCount = this.state.categoryImages.length;
        let numShown = Math.floor(this.state.screenWidth / this.widthPerImage);
        numShown = Math.min(numShown, imageCount);
        numShown = Math.max(numShown, 1);
        numShown = Math.min(numShown, maxImagesShown);
        const imageContianerWidth = Math.max(1, imageCount / numShown) * 100;
        const marginPerImage = imageContianerWidth / imageCount;
        const marginOffset = marginPerImage * this.state.imageOffset;
        const style: React.CSSProperties = {
            width: `${imageContianerWidth}%`,
            marginLeft: `-${marginOffset}%`
        };

        const _touchStartHandler = (event: React.TouchEvent<HTMLElement>) => {
            const ele = event.currentTarget;
            const startX = event.touches[0].clientX;
            const minOffset = 0;
            const maxOffset = marginPerImage * (imageCount - numShown);
            const scrollSlop = marginPerImage / 2;
            const scrollThreshold = marginPerImage / 4;
            let deltaX = 0;
            let percentX = (deltaX / ele.clientWidth) * 100;

            const _touchMoveHandler = (event: TouchEvent) => {
                if (event.touches[0] && ele.parentElement) {
                    deltaX = event.touches[0].clientX - startX;
                    percentX = (deltaX / ele.parentElement.clientWidth) * 100;
                }
                let marginLeft = marginOffset - percentX;
                marginLeft = Math.max(marginLeft, minOffset - scrollSlop);
                marginLeft = Math.min(marginLeft, maxOffset + scrollSlop);

                if (ele && ele instanceof HTMLElement) {
                    // disable anchor click if past scroll threshold
                    if (scrollThreshold < percentX || scrollThreshold < -percentX) {
                        ele.classList.add('disable');
                    }

                    // scroll the content based on mouse movement
                    if (marginLeft < 0) {
                        marginLeft = Math.abs(marginLeft);
                        ele.style.marginLeft = `${marginLeft}%`;
                    } else {
                        ele.style.marginLeft = `-${marginLeft}%`;
                    }
                }
            };

            const _touchEndHandler = (event: TouchEvent) => {
                let stateChanged = false;
                if (event.touches[0] && ele.parentElement) {
                    deltaX = event.touches[0].clientX - startX;
                    percentX = (deltaX / ele.parentElement.clientWidth) * 100;
                }

                if (scrollThreshold < percentX) {
                    // scroll -Y
                    if (this.state.imageOffset > 0) {
                        let offset = this.state.imageOffset - 1;
                        offset -= Math.floor((percentX - scrollThreshold) / marginPerImage);
                        offset = Math.max(offset, 0);
                        this.setState({ ...this.state, imageOffset: offset });
                        stateChanged = true;
                    }
                }

                if (scrollThreshold < -percentX) {
                    // scroll +Y
                    const lastOffset = this.state.categoryImages.length - numShown;
                    if (this.state.imageOffset < lastOffset) {
                        let offset = this.state.imageOffset + 1;
                        offset -= Math.ceil((percentX + scrollThreshold) / marginPerImage);
                        offset = Math.min(offset, lastOffset);
                        this.setState({ ...this.state, imageOffset: offset });
                        stateChanged = true;
                    }
                }

                document.removeEventListener('touchmove', _touchMoveHandler);
                document.removeEventListener('touchend', _touchEndHandler);
                if (ele && ele instanceof HTMLElement) {
                    ele.classList.remove('disable');
                    ele.classList.remove('touched');
                    if (stateChanged === false) {
                        ele.style.marginLeft = `-${marginOffset}%`;
                    }
                }
                event.stopPropagation();
            };

            document.addEventListener('touchmove', _touchMoveHandler);
            document.addEventListener('touchend', _touchEndHandler);
            if (ele && ele instanceof HTMLElement) {
                ele.classList.add('touched');
            }
            event.stopPropagation();
        };

        const _mouseDownHandler = (event: React.MouseEvent<HTMLElement>) => {
            const ele = event.currentTarget;
            const startX = event.clientX;
            const minOffset = 0;
            const maxOffset = marginPerImage * (imageCount - numShown);
            const scrollSlop = marginPerImage / 2;
            const scrollThreshold = marginPerImage / 4;
            let deltaX = 0;
            let percentX = (deltaX / ele.clientWidth) * 100;

            const _mouseMoveHandler = (event: MouseEvent) => {
                if (ele.parentElement) {
                    deltaX = event.clientX - startX;
                    percentX = (deltaX / ele.parentElement.clientWidth) * 100;
                }
                let marginLeft = marginOffset - percentX;
                marginLeft = Math.max(marginLeft, minOffset - scrollSlop);
                marginLeft = Math.min(marginLeft, maxOffset + scrollSlop);

                if (ele && ele instanceof HTMLElement) {
                    // disable anchor click if past scroll threshold
                    if (scrollThreshold < percentX || scrollThreshold < -percentX) {
                        ele.classList.add('disable');
                    }

                    // scroll the content based on mouse movement
                    if (marginLeft < 0) {
                        marginLeft = Math.abs(marginLeft);
                        ele.style.marginLeft = `${marginLeft}%`;
                    } else {
                        ele.style.marginLeft = `-${marginLeft}%`;
                    }
                }
            };

            const _mouseUpHandler = (event: MouseEvent) => {
                let stateChanged = false;
                if (ele.parentElement) {
                    deltaX = event.clientX - startX;
                    percentX = (deltaX / ele.parentElement.clientWidth) * 100;
                }

                if (scrollThreshold < percentX) {
                    // scroll -Y
                    if (this.state.imageOffset > 0) {
                        let offset = this.state.imageOffset - 1;
                        offset -= Math.floor((percentX - scrollThreshold) / marginPerImage);
                        offset = Math.max(offset, 0);
                        this.setState({ ...this.state, imageOffset: offset });
                        stateChanged = true;
                    }
                }

                if (scrollThreshold < -percentX) {
                    // scroll +Y
                    const lastOffset = this.state.categoryImages.length - numShown;
                    if (this.state.imageOffset < lastOffset) {
                        let offset = this.state.imageOffset + 1;
                        offset -= Math.ceil((percentX + scrollThreshold) / marginPerImage);
                        offset = Math.min(offset, lastOffset);
                        this.setState({ ...this.state, imageOffset: offset });
                        stateChanged = true;
                    }
                }

                document.removeEventListener('mousemove', _mouseMoveHandler);
                document.removeEventListener('mouseup', _mouseUpHandler);
                if (ele && ele instanceof HTMLElement) {
                    ele.classList.remove('disable');
                    ele.classList.remove('touched');
                    if (stateChanged === false) {
                        ele.style.marginLeft = `-${marginOffset}%`;
                    }
                }
                event.stopPropagation();
            };

            document.addEventListener('mousemove', _mouseMoveHandler);
            document.addEventListener('mouseup', _mouseUpHandler);
            if (ele && ele instanceof HTMLElement) {
                ele.classList.add('touched');
            }
            event.stopPropagation();
        };

        const _renderPageLeft = (): JSX.Element | null => {
            const _handleClick = (event: React.MouseEvent<HTMLElement>) => {
                this.setState({ ...this.state, imageOffset: this.state.imageOffset - 1 });
                event.preventDefault();
                event.stopPropagation();
            };

            if (this.state.imageOffset <= 0 || this.props.navProps.config.pagerType !== pagerType.arrow) {
                return null;
            }

            return (
                <div className='nss-navigation-menu__image-pager-left' onClick={_handleClick}>
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' width='48px'>
                        <path d='M8.3685 12L13.1162 3.03212L14.8838 3.9679L10.6315 12L14.8838 20.0321L13.1162 20.9679L8.3685 12Z'></path>
                    </svg>
                </div>
            );
        };

        const _renderPageRight = (): JSX.Element | null => {
            const _handleClick = (event: React.MouseEvent<HTMLElement>) => {
                this.setState({ ...this.state, imageOffset: this.state.imageOffset + 1 });
                event.preventDefault();
                event.stopPropagation();
            };

            if (
                this.state.imageOffset >= this.state.categoryImages.length - numShown ||
                this.props.navProps.config.pagerType !== pagerType.arrow
            ) {
                return null;
            }

            return (
                <div className='nss-navigation-menu__image-pager-right' onClick={_handleClick}>
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' width='48px'>
                        <path d='M15.6315 12L10.8838 3.03212L9.11622 3.9679L13.3685 12L9.11622 20.0321L10.8838 20.9679L15.6315 12Z'></path>
                    </svg>
                </div>
            );
        };

        const _renderBubbleContainer = () => {
            const _onBubbleClick = (offset: number) => {
                this.setState({ ...this.state, imageOffset: offset });
            };

            if (this.props.navProps.config.pagerType !== pagerType.bubble) {
                return null;
            }

            const items = [];

            for (let i = 0; i <= imageCount - numShown; i++) {
                const selectedClass = this.state.imageOffset === i ? ' selected' : '';
                items.push(
                    <div key={i} className={`nss-navigation-menu__image-bubble${selectedClass}`} onClick={() => _onBubbleClick(i)}></div>
                );
            }

            if (items.length <= 1) {
                return null;
            }

            return <div className='nss-navigation-menu__image-bubble-container'>{items}</div>;
        };

        return (
            <div className='nss-navigation-menu__image-wrap'>
                <div className='nss-navigation-menu__image-inner-wrap'>
                    <div
                        id='nss-navigation-menu__image-container'
                        className='nss-navigation-menu__image'
                        style={style}
                        onTouchStart={_touchStartHandler}
                        onMouseDown={_mouseDownHandler}
                    >
                        {_renderPageLeft()}
                        {this.state.categoryImages.map(image => (
                            <a href={image.href}>
                                <Msdyn365.Image
                                    key={image.src}
                                    requestContext={this.props.navProps.context.actionContext.requestContext}
                                    className='ms-nav-image__item'
                                    {...image}
                                    gridSettings={this.props.navProps.context.request.gridSettings!}
                                    imageSettings={this.props.navProps.config.categoryImageSettings ?? defaultImageSettings}
                                    loadFailureBehavior='hide'
                                    role='tabpanel'
                                />
                            </a>
                        ))}
                        {_renderPageRight()}
                    </div>
                </div>
                {_renderBubbleContainer()}
            </div>
        );
    }
}

export default navigationMenuView;
